import React, { useMemo } from "react";
import { find, get, reduce } from "lodash";
import MultipleLineChart from "@charty/line-plot";
import LineChartTooltip from "@charty/tooltip/chartjs-simple-serie";
import { Color } from "@glossy";
import { getMetricLabel } from "@linkfluence/i18n";
import { AbstractDate } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { colorFromSeed } from "@mocky";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizMultipleLineChart } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentDateTerm } from "@radarly/models/search";
import { useRangeWithTimeUnit, useTimezoneFromFragment } from "../../data";
import Empty from "../../empty";
import Tooltip from "../../tooltip/metric";
import { computeBucketDataByTerm } from "../computation";

export interface SharedWidgetsFieldMultipleLineChartInnerProps {
  loading: boolean;
  data: SearchFragmentDateTerm["data"];
  fragment: SearchFragmentDateTerm;
  dataviz: DatavizMultipleLineChart;
  facet: FullFacet;
  onClickLegend?: (uid: string | number) => void;
  onClickElement?: (
    uid: string | number | Array<string | number>,
    range: { from: Date; to: Date }
  ) => void;
  className?: string;
  disableAnimation?: boolean;
  nowDate?: AbstractDate;
  isFullscreen?: boolean;
}

function isNumeric(x: string) {
  return x !== "" ? !isNaN(Number(x)) : false;
}

/**
 * Facets that cannot have predefined values. For example :
 *  Predefined
 *    - Platforms have a fixed list of values
 *    - Queries and CustomFields have dynamic values but they are found in the Project
 *  Not Predefined
 *    - Hashtags values are first seen in the returned data
 */
const FACETS_WITHOUT_PREDEFINED_VALUES = [Facet.Hashtags, Facet.Keyphrases];

const getValueColor = (
  dataviz: DatavizMultipleLineChart,
  term: string | number,
  facet: Facet
): string => {
  if (dataviz.filteredValues) {
    return (
      dataviz.filteredValues.find((filteredValue) => {
        const fValue =
          facet === Facet.Hashtags
            ? filteredValue.value.replace("#", "")
            : filteredValue.value;
        return fValue === term;
      })?.color || colorFromSeed(term)
    );
  }
  return colorFromSeed(term); // Deterministic random color: terms keep their color after a re-render
};

const dataByFacet = (
  data: SearchFragmentDateTerm["data"],
  metric: Metric,
  facet: FullFacet,
  dataviz: DatavizMultipleLineChart
) => {
  const dataByTerm = computeBucketDataByTerm(data, facet.id, metric);
  return reduce(
    dataByTerm,
    (acc, values, term) => {
      const _term =
        isNumeric(term) &&
        term !== get(facet, "values[0].id") &&
        facet.id !== Facet.ReviewRate
          ? parseInt(term)
          : term;
      if (FACETS_WITHOUT_PREDEFINED_VALUES.includes(facet.id as Facet)) {
        acc.push({
          id: _term,
          label: facet.id === Facet.Hashtags ? `#${_term}` : _term,
          color: getValueColor(dataviz, _term, facet.id as Facet),
          data: values,
          total: values?.reduce((acc, cur) => (acc += cur.count), 0) || 0,
        });
      } else {
        const value = find(facet.values, { id: _term });
        if (value) {
          acc.push({
            id: _term,
            label: value.label,
            color: value.color as Color,
            data: values,
            total: values?.reduce((acc, cur) => (acc += cur.count), 0) || 0,
          });
        }
      }
      return acc;
    },
    []
  );
};

const CONFIGURATION = {
  hover: { mode: "nearest", axis: "x", intersect: true },
  tooltips: { mode: "nearest", axis: "x", intersect: true },
} as const;

export default function SharedWidgetsFieldMultipleLineChartInner({
  data,
  loading,
  fragment,
  dataviz,
  facet,
  className,
  onClickElement,
  onClickLegend,
  disableAnimation,
  nowDate,
  isFullscreen,
}: SharedWidgetsFieldMultipleLineChartInnerProps) {
  const locale = useCurrentUserLocale();
  const range = useRangeWithTimeUnit(fragment, loading, nowDate);
  const metric = fragment
    ? fragment.request.aggs.date.metrics[0]
    : Metric.Impression;
  const series = useMemo(() => {
    return dataByFacet(data, metric, facet, dataviz);
  }, [data, metric, facet, dataviz]);

  const timezone = useTimezoneFromFragment(fragment);
  return (
    <Empty empty={!series.length}>
      <MultipleLineChart
        chartTooltip={(series) => (
          <LineChartTooltip
            series={series}
            locale={locale}
            metricLabel={getMetricLabel(locale, metric)}
          />
        )}
        timezone={timezone}
        locale={locale}
        scale={dataviz.scale || "linear"}
        className={className}
        series={series}
        range={range}
        onClickPoint={onClickElement}
        onClickLegend={onClickLegend}
        tooltip={(serie) => (
          <Tooltip
            range={range}
            count={serie.count}
            color={serie.color}
            label={serie.label}
            metric={metric}
            locale={locale}
          />
        )}
        config={CONFIGURATION}
        disableAnimation={disableAnimation}
        isFullscreen={isFullscreen}
        displayVolumes={dataviz.displayVolumes}
        legendSort={dataviz.legendSort}
      />
    </Empty>
  );
}
